import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Configuration } from 'src/app/app.constants';
import { ISensorDetail, ISensorKindGoals, ISensorManufacturer, ISensorType } from 'src/app/core/models/sensor.model';
import { SensorTypeModel } from 'src/app/core/sensor-type.model';
import { ResponseMessage } from 'src/app/shared/genericentity/genericentity';

@Injectable({
  providedIn: 'root',
})
export class SensorDataService {
  constructor(
    private http: HttpClient,
    private configuration: Configuration,
  ) {}

  /**
   * Method to add new sensor
   *
   * @param body sensor data
   */
  addSensor(body: ISensorDetail): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor';
    return this.http.post<ResponseMessage>(_url, body);
  }

  /**
   * Method to update sensor
   *
   * @param body sensor data
   */
  updateSensor(body: ISensorDetail): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor';
    return this.http.put<ResponseMessage>(_url, body);
  }

  /**
   * Method to get sensor list
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorList(body: string, page: number, size: number): Observable<ISensorDetail[]> {
    const _url: string = this.configuration.Server + 'corporation/all/sensors/' + page + '/' + size;
    return this.http.post<ISensorDetail[]>(_url, body);
  }

  /**
   * Method to get sensor list for dropdown
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorDropDown(body: string, page: number, size: number): Observable<ISensorDetail[]> {
    const _url: string = this.configuration.Server + 'corporation/sensors/' + page + '/' + size;
    return this.http.post<ISensorDetail[]>(_url, body);
  }

  /**
   * Method to get sensor detail
   *
   * @param body sensor identifier
   */
  getSensorSingle(body: ISensorDetail): Observable<ISensorDetail> {
    const _url: string = this.configuration.Server + 'corporation/sensor/details';
    return this.http.post<ISensorDetail>(_url, body);
  }

  /**
   * Method to get air quality sensor detail
   *
   * @param body sensor identifier
   */
  getAirQualitySensorSingle(body: ISensorDetail): Observable<ISensorDetail> {
    const _url: string = this.configuration.Server + 'corporation/airquality/sensor/details';
    return this.http.post<ISensorDetail>(_url, body);
  }

  /**
   * Method to remove sensor image
   *
   * @param sensorId sensor id
   */
  removeSensorImage(sensorId: number): Observable<ResponseMessage> {
    const toUpdate = JSON.stringify({});
    const _url: string = this.configuration.Server + 'corporation/remove/sensor/' + sensorId + '/image';
    return this.http.put<ResponseMessage>(_url, toUpdate);
  }

  /**
   * Method to upload sensor image
   *
   * @param filedata sensor image file
   * @param id sensor id
   */
  uploadSensorImage(filedata: File, id: number): Promise<ResponseMessage> {
    return new Promise((resolve, reject) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response) as ResponseMessage);
          } else {
            reject(xhr.response);
          }
        }
      };
      const _url: string = this.configuration.Server + 'corporation/sensor/' + id + '/image';

      xhr.open('PUT', _url, true);
      xhr.setRequestHeader('Authorization', 'bearer ' + this.configuration.Token);

      const formData = new FormData();
      formData.append('file', filedata, filedata.name);
      xhr.send(formData);
    });
  }

  /**
   * Method to add sensor type
   *
   * @param body sensor type data
   */
  addSensorType(body: ISensorType): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor/type';
    return this.http.post<ResponseMessage>(_url, body);
  }

  /**
   * Method to update sensor type
   *
   * @param body sensor type data
   */
  updateSensorType(body: ISensorType): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor/type';
    return this.http.put<ResponseMessage>(_url, body);
  }

  /**
   * Method to get sensor type list
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorTypesList(body: string, page: number, size: number): Observable<ISensorType[]> {
    const _url: string = this.configuration.Server + 'corporation/all/sensor/types/' + page + '/' + size;
    return this.http.post<ISensorType[]>(_url, body);
  }

  /**
   * Method to get sensor type list
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorTypes(body: string, page: number, size: number): Observable<SensorTypeModel[]> {
    const _url: string = this.configuration.Server + 'corporation/all/sensor/types/' + page + '/' + size;
    return this.http.post<SensorTypeModel[]>(_url, body);
  }

  /**
   * Method to get sensor type list for dropdown
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorTypesDropDown(body: string, page: number, size: number): Observable<ISensorType[]> {
    const _url: string = this.configuration.Server + 'corporation/sensor/types/' + page + '/' + size;
    return this.http.post<ISensorType[]>(_url, body);
  }

  /**
   * Method to get sensor type detail
   *
   * @param body sensor type identifier
   */
  getSensorTypeSingle(body: ISensorType): Observable<ISensorType> {
    const _url: string = this.configuration.Server + 'corporation/sensor/type/details';
    return this.http.post<ISensorType>(_url, body);
  }

  /**
   * Method to remove sensor type image
   *
   * @param sensorTypeId sensor type id
   */
  removeSensorTypeImage(sensorTypeId: number): Observable<ResponseMessage> {
    const toUpdate = JSON.stringify({});
    const _url: string = this.configuration.Server + 'corporation/remove/sensor/type/' + sensorTypeId + '/image';
    return this.http.put<ResponseMessage>(_url, toUpdate);
  }

  /**
   * Method to upload sensor type image
   *
   * @param filedata sensor type image file
   * @param id sensor type id
   */
  uploadSensorTypeImage(filedata: File, id: number): Promise<ResponseMessage> {
    return new Promise((resolve, reject) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response) as ResponseMessage);
          } else {
            reject(xhr.response);
          }
        }
      };
      const _url: string = this.configuration.Server + 'corporation/sensor/type/' + id + '/image';

      xhr.open('PUT', _url, true);
      xhr.setRequestHeader('Authorization', 'bearer ' + this.configuration.Token);

      const formData = new FormData();
      formData.append('file', filedata, filedata.name);
      xhr.send(formData);
    });
  }

  /**
   * Method to add sensor manufacturer
   *
   * @param sensorManufacturer sensor manufacturer data
   */
  addSensorManufacturer(sensorManufacturer: ISensorManufacturer): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor/manufacturer';
    return this.http.post<ResponseMessage>(_url, sensorManufacturer);
  }

  /**
   * Method to update sensor manufacturer
   *
   * @param sensorManufacturer sensor manufacturer data
   */
  updateSensorManufacturer(sensorManufacturer: ISensorManufacturer): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor/manufacturer';
    return this.http.put<ResponseMessage>(_url, sensorManufacturer);
  }

  /**
   * Method to get sensor manufacturer list
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorManufacturerList(body: string, page: number, size: number): Observable<ISensorManufacturer[]> {
    const _url: string = this.configuration.Server + 'corporation/all/sensor/manufactureres/' + page + '/' + size;
    return this.http.post<ISensorManufacturer[]>(_url, body);
  }

  /**
   * Method to get sensor manufacturer list for dropdown
   *
   * @param body search payload
   * @param page page index
   * @param size page size
   */
  getSensorManufacturerDropdown(body: string, page: number, size: number): Observable<ISensorManufacturer[]> {
    const _url: string = this.configuration.Server + 'corporation/sensor/manufactureres/' + page + '/' + size;
    return this.http.post<ISensorManufacturer[]>(_url, body);
  }

  /**
   * Method to get sensor manufacturer detail
   *
   * @param body sensor manufacturer identifier
   */
  getSensorManufacturerSingle(body: ISensorManufacturer): Observable<ISensorManufacturer> {
    const _url: string = this.configuration.Server + 'corporation/sensor/manufacturer/details';
    return this.http.post<ISensorManufacturer>(_url, body);
  }

  /**
   * Method to get sensor kind goals sample data
   */
  getSensorKindGoalsSample(): Observable<ISensorKindGoals[]> {
    const _url: string = this.configuration.Server + 'corporation/sensor/kind/goals';
    return this.http.get<ISensorKindGoals[]>(_url);
  }

  /**
   * Method to insert sensor kind goals
   *
   * @param body sensor kind goals payload
   */
  insertSensorKindGoals(body: ISensorKindGoals[]): Observable<ResponseMessage> {
    const _url: string = this.configuration.Server + 'corporation/sensor/kind/goals';
    return this.http.post<ResponseMessage>(_url, body);
  }
}
